import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { getFormattedDateWithHours } from 'src/utils/date-formatter.util'
import { CalendarIcon, CurecallTooltip, QuestionMarkIcon, TimeIcon } from '@honestica/curecall-ui'
import { useSession } from 'src/providers/auth.provider'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { EPatientMedEventOrigin, TPatientMedEvent } from 'src/types/med-event'
import { TFollowup } from 'src/types/followup'
import { getDoctorCivilityAndName, getFullAddress } from 'src/utils/string-formatter.util'
import { TimezoneAwareTime } from 'src/components/timezone-aware-time/timezone-aware-time.component'
import { getConvocationLabel, getDateLabel } from '../patient-med-event.helper'
import { twMerge } from 'src/utils/tw-merge.util'
import { useTimezonePreference } from 'src/providers/timezone-preference.provider'
import { ResourceChangeHistoryButton } from '../../../_components/resource-change-history/resource-change-history-button.component'
import { AdminOnlyBadge } from 'src/components/admin-only-badge.component'

function getPrettyOrigin(
  t: TFunction,
  patientMedEvent: TPatientMedEvent,
  showDebugData: boolean,
  timezone?: string,
): ReactNode {
  switch (patientMedEvent.origin) {
    case EPatientMedEventOrigin.USER: {
      const baseText = t('components.patient.patient-file.body.appointment-info.pretty-origin.user', {
        date: getFormattedDateWithHours(t, patientMedEvent.createdAt, false, timezone),
      })
      if (!showDebugData) return baseText
      return (
        <>
          {baseText}{' '}
          <span className="c-inline-flex c-items-center c-gap-1">
            <AdminOnlyBadge variant="tooltip" />
            {t('components.patient.patient-file.body.appointment-info.pretty-origin.user-debug', {
              userId: patientMedEvent.CreatorId,
            })}
          </span>
        </>
      )
    }
    case EPatientMedEventOrigin.PATIENT:
      return t('components.patient.patient-file.body.appointment-info.pretty-origin.patient', {
        date: getFormattedDateWithHours(t, patientMedEvent.createdAt, false, timezone),
      })
    default:
      return t('components.patient.patient-file.body.appointment-info.pretty-origin.interoperability', {
        date: getFormattedDateWithHours(t, patientMedEvent.createdAt, false, timezone),
        remoteId: patientMedEvent.remoteId,
      })
  }
}

type Props = {
  patientMedEvent: TPatientMedEvent
  currentFollowup: TFollowup
  children?: ReactNode
}
const AppointmentInfo = ({ patientMedEvent, currentFollowup, children }: Props) => {
  const { i18n, t } = useTranslation()
  const locale = i18n.language
  const { Location, MedicalStaffMember, MedEventId, date, convocationDate } = patientMedEvent
  const { project, getMedEventLabelById } = useCurrentProject()
  const { currentUser } = useSession()
  const { displayTimezone } = useTimezonePreference()

  const functionalUnitId =
    patientMedEvent?.metadata?.functionalUnitId ?? patientMedEvent.interopMetadata?.visit?.ufMedId
  const functionalUnit = patientMedEvent?.metadata?.functionalUnit
  let functionalUnitLabel = ''
  if (functionalUnitId && functionalUnit) {
    functionalUnitLabel = `UF: ${functionalUnitId} - ${functionalUnit}`
  } else if (functionalUnitId) {
    functionalUnitLabel = `UF: ${functionalUnitId}`
  } else if (functionalUnit) {
    functionalUnitLabel = `UF: ${functionalUnit}`
  }

  const originalLabel = patientMedEvent?.metadata?.originalLabel
  const visitNumber = patientMedEvent.interopMetadata?.visit?.number
  const medEventUI = currentFollowup.configUI.medEventsUI.find(
    (medEventUI) => medEventUI.MedEvent.id === patientMedEvent.MedEventId,
  )

  return (
    <div className="c-flex c-flex-1 c-text-sm c-flex-col c-gap-2 c-pb-4 c-border-b c-border-b-grey-300 last-of-type:c-border-none c-mt-4">
      {!!MedEventId && (
        <div className="c-flex c-items-center c-gap-2">
          <h4 className="c-mb-0 c-text-sm c-flex c-items-center c-font-bold">
            <CalendarIcon className="c-w-4 c-mr-2" />
            {getMedEventLabelById(patientMedEvent.MedEventId)}
            <CurecallTooltip
              tooltipPosition="right"
              content={getPrettyOrigin(t, patientMedEvent, !!currentUser?.isAdmin, displayTimezone)}
              wrapperClassName="tw-ml-2 tw-inline-block"
            >
              <div
                className={twMerge(patientMedEvent.remoteId && 'c-cursor-pointer')}
                onClick={() => {
                  patientMedEvent.remoteId && navigator.clipboard.writeText(patientMedEvent.remoteId)
                }}
              >
                <QuestionMarkIcon className="tw-h-4 tw-w-4 c-mr-2" />
              </div>
            </CurecallTooltip>
          </h4>
          <ResourceChangeHistoryButton
            projectId={project.id}
            patientId={patientMedEvent.PatientId}
            modelName="PatientMedEvent"
            recordId={String(patientMedEvent.id)}
          />
        </div>
      )}
      {(originalLabel || functionalUnitLabel || visitNumber) && (
        <div className="c-border-l-2">
          {originalLabel && <p className="c-ml-2 c-mb-1">{originalLabel}</p>}
          {functionalUnitLabel && <p className="c-ml-2 c-mb-1">{functionalUnitLabel}</p>}
          {visitNumber && (
            <p className="c-ml-2 c-mb-1">
              {t('components.patient.patient-file.body.appointment-info.visit-number')} : {visitNumber}
            </p>
          )}
        </div>
      )}
      {date && (
        <p className="c-mb-0 c-flex c-items-center">
          <TimeIcon className="c-w-4 c-mr-2" />
          {getDateLabel({ t, medEventUI, locale })} :
          <span className="c-ml-1">
            <TimezoneAwareTime date={date} formatter="dateWithHours" />
          </span>
        </p>
      )}
      {convocationDate && (
        <p className="c-mb-0 c-flex c-items-center">
          <TimeIcon className="c-w-4 c-mr-2" />
          {getConvocationLabel({ t, medEventUI, locale })} :
          <span className="c-ml-1">
            <TimezoneAwareTime date={convocationDate} formatter="dateWithHours" />
          </span>
        </p>
      )}
      <div className="c-flex">
        {MedicalStaffMember && (
          <div className="c-text-grey-500 c-text-xs">
            {t('components.patient.patient-file.body.followup-details.with-doctor', {
              doctorName: getDoctorCivilityAndName(MedicalStaffMember),
            })}
          </div>
        )}
        {MedicalStaffMember && Location && <div className="c-mx-2 c-w-[1px] c-bg-grey-300" />}
        {Location && <div className="c-text-grey-500 c-text-xs">{getFullAddress(Location)}</div>}
      </div>
      {children}
    </div>
  )
}

export default AppointmentInfo
